// @tennis-matches/services/governance
// Admin: menschenlesbare Vereins-/Tenant-Labels zu scope_id (RPC public.resolve_tenant_scope_labels).

import { getSupabaseClient } from "../supabase/index.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

function __isMissingRpcError(error) {
  if (!error) return false;
  const code = __asStr(error.code);
  const msg = __asStr(error.message).toLowerCase();
  return (
    code === "42883" ||
    code === "PGRST202" ||
    msg.includes("could not find the function") ||
    msg.includes("function not found")
  );
}

const LABELS_CACHE_MS = 30000;
/** @type {Map<string, Promise<object>>} */
const __labelsInflight = new Map();
/** @type {Map<string, { ts: number, value: object }>} */
const __labelsCache = new Map();

/**
 * @param {string[]} scopeIds
 * @returns {Promise<{ ok: boolean, byScopeId?: Record<string, { scope_id: string, tenant_id: string, display_name: string, city: string, display_label: string }>, reason?: string, error?: unknown }>}
 */
export async function resolveTenantScopeLabelsRemote(scopeIds) {
  const uniq = Array.from(
    new Set(
      (Array.isArray(scopeIds) ? scopeIds : [])
        .map(__asStr)
        .filter(
          (x) =>
            x &&
            x !== "__pending_new_club__" &&
            x !== "__independent_trainer__",
        ),
    ),
  );
  if (!uniq.length) {
    return { ok: true, byScopeId: {} };
  }

  const cacheKey = uniq.slice().sort().join("|");
  const now = Date.now();
  const cached = __labelsCache.get(cacheKey);
  if (cached && now - cached.ts < LABELS_CACHE_MS) {
    return cached.value;
  }
  const inflight = __labelsInflight.get(cacheKey);
  if (inflight) return inflight;

  const promise = __resolveTenantScopeLabelsImpl(uniq)
    .then((value) => {
      if (value?.ok) {
        __labelsCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__labelsInflight.get(cacheKey) === promise) {
        __labelsInflight.delete(cacheKey);
      }
    });

  __labelsInflight.set(cacheKey, promise);
  return promise;
}

async function __resolveTenantScopeLabelsImpl(uniq) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", byScopeId: {} };

  const { data, error } = await supabase.rpc("resolve_tenant_scope_labels", {
    p_scope_ids: uniq,
  });

  if (error && __isMissingRpcError(error)) {
    return { ok: true, byScopeId: {}, reason: "rpc-missing", error };
  }
  if (error) {
    return { ok: false, reason: "rpc-error", error, byScopeId: {} };
  }

  const byScopeId = {};
  for (const row of Array.isArray(data) ? data : []) {
    const sid = __asStr(row?.scope_id);
    if (!sid) continue;
    const entry = {
      scope_id: sid,
      tenant_id: __asStr(row?.tenant_id),
      display_name: __asStr(row?.display_name),
      city: __asStr(row?.city),
      display_label:
        __asStr(row?.display_label) || __asStr(row?.display_name) || sid,
    };
    byScopeId[sid] = entry;
    const lk = sid.toLowerCase();
    if (lk && lk !== sid) byScopeId[lk] = entry;
  }
  return { ok: true, byScopeId };
}
